// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — small panels that stand beside the globe in the headset, each
 * with its own content: a video, a picture or a few lines of text (the Khipu
 * plan, slice 4). Geo-media shows a radio or camera's picture and
 * its label through them (`vrGeoMedia`).
 *
 * A panel is placed by an offset from the globe's centre in metres (x to
 * the right, y up, z toward the room's front), the way the geo-media panel
 * has always stood, and turns to face the viewer every frame. Written from
 * scratch for AYNI from the geo-media panel's needs: nothing here comes
 * from TerraViz's headset overlay code.
 *
 * AYNI (owner on Quest, 2026-11-04):
 *   - a text panel may carry a ✕ (`closable`): the satellite card has
 *     one, which lets the satellite and its orbit go. `pick` finds it under
 *     a ray, and the panel's body under the ray takes the press too, so a
 *     press on a card never reaches the globe or a satellite behind it;
 *   - inside a globe grown past 2 m (`vrInsideView`) the panels stand
 *     around the viewer instead, each offset read from a point ahead of
 *     them in their own frame (x to their right, z toward them), at size 1.
 */
import type * as THREE from 'three'
import { markPanel, unmarkPanel } from './vrPanelLayout'
import { createCanvasPanel, PANEL_COLORS, type CanvasPanel } from './vrPanelKit'
import type { InsideFrame } from './vrInsideView'
import { wrapText } from './vrKhipuPanel'

export interface WorldPanelPlace {
  /** From the globe's centre, in metres. */
  offset: { x: number; y: number; z: number }
  /** The plane, in metres. */
  size: { width: number; height: number }
}

export interface VrWorldPanelsHandle {
  readonly group: THREE.Group
  /** Show (or replace) panel `id` playing `video`. */
  showVideo(id: string, video: HTMLVideoElement, place: WorldPanelPlace): void
  /** Show (or replace) panel `id` with a decoded picture. */
  showImage(id: string, image: HTMLImageElement, place: WorldPanelPlace): void
  /** Show (or replace) panel `id` with lines of text, framed; `closable` gives it a ✕ in its top corner. */
  showText(id: string, text: string, place: WorldPanelPlace, opts?: { closable?: boolean }): void
  hide(id: string): void
  has(id: string): boolean
  /**
   * Stand every panel at its offset from the globe (times the globe's
   * scale, so a grown globe never swallows it), facing the viewer; with
   * `inside`, eased (by its `blend`) toward the same offset around the viewer.
   */
  update(globeCentre: THREE.Vector3, viewer: THREE.Vector3, scale?: number, inside?: InsideFrame | null): void
  /** The panel under the ray: its id, and whether the ray is on its ✕. Only panels with a ✕ take presses. */
  pick(raycaster: THREE.Raycaster): { id: string; close: boolean } | null
  /** The panels that take presses, for the laser dot to land on. */
  pressable(out: THREE.Object3D[]): void
  dispose(): void
}

interface Shown {
  mesh: THREE.Mesh
  place: WorldPanelPlace
  /** A panel with a ✕: what is under a UV ('close' on the ✕, 'body' elsewhere). */
  hitTest?: (uv: { x: number; y: number }) => 'close' | 'body' | null
  dispose(): void
}

/** Text canvas pixels per metre of panel. */
const TEXT_PX_PER_M = 1400

export function createVrWorldPanels(THREE_: typeof THREE): VrWorldPanelsHandle {
  const group = new THREE_.Group()
  const shown = new Map<string, Shown>()
  const scratch = new THREE_.Vector3()
  const around = new THREE_.Vector3()

  function put(id: string, item: Shown): void {
    hide(id)
    item.mesh.renderOrder = 10
    group.add(item.mesh)
    shown.set(id, item)
  }

  function hide(id: string): void {
    const item = shown.get(id)
    if (!item) return
    group.remove(item.mesh)
    item.dispose()
    shown.delete(id)
  }

  function textured(texture: THREE.Texture, place: WorldPanelPlace): Shown {
    texture.colorSpace = THREE_.SRGBColorSpace
    const material = new THREE_.MeshBasicMaterial({ map: texture, transparent: true, depthTest: false, depthWrite: false, side: THREE_.DoubleSide })
    const geometry = new THREE_.PlaneGeometry(place.size.width, place.size.height)
    const mesh = new THREE_.Mesh(geometry, material)
    // AYNI (owner, 2026-11-05): a picture or video panel is kept off the others too (vrPanelLayout).
    markPanel(mesh, { width: place.size.width, height: place.size.height })
    return {
      mesh,
      place,
      dispose: () => {
        unmarkPanel(mesh)
        texture.dispose()
        material.dispose()
        geometry.dispose()
      },
    }
  }

  return {
    group,
    showVideo(id, video, place) {
      put(id, textured(new THREE_.VideoTexture(video), place))
    },
    showImage(id, image, place) {
      const texture = new THREE_.Texture(image)
      texture.needsUpdate = true
      put(id, textured(texture, place))
    },
    showText(id, text, place, opts) {
      const canvasW = Math.round(place.size.width * TEXT_PX_PER_M)
      const canvasH = Math.round(place.size.height * TEXT_PX_PER_M)
      const closable = opts?.closable === true
      const panel: CanvasPanel<'close' | 'body'> = createCanvasPanel(THREE_, { widthM: place.size.width, heightM: place.size.height, canvasW, canvasH })
      panel.draw((p) => {
        p.frame(24)
        const pad = 28
        // The ✕ in the top corner; the body under it takes a press too (drawn first, so the ✕ wins).
        const closeSize = 64
        if (closable) {
          p.hit(0, 0, canvasW, canvasH, 'body')
          p.button(canvasW - pad - closeSize + 8, pad - 12, closeSize, closeSize, '', 'close', { icon: 'close', radius: closeSize / 2 })
        }
        p.g.font = p.font(30)
        const lines = text.split('\n').flatMap((l) => wrapText((s) => p.g.measureText(s).width, l, canvasW - pad * 2))
        const room = Math.max(1, Math.floor((canvasH - pad * 2) / 40))
        lines.slice(0, room).forEach((line, i) => {
          // The first line (the name) stops short of the ✕.
          const maxWidth = canvasW - pad * 2 - (closable && i === 0 ? closeSize : 0)
          p.text(line, pad, pad + 20 + i * 40, { size: 30, weight: i === 1 ? 600 : 500, color: i === 0 ? PANEL_COLORS.accentText : PANEL_COLORS.text, maxWidth })
        })
      })
      put(id, { mesh: panel.mesh, place, hitTest: closable ? (uv) => panel.hitTest(uv) : undefined, dispose: () => panel.dispose() })
    },
    hide,
    has: (id) => shown.has(id),
    update(globeCentre, viewer, scale = 1, inside = null) {
      const k = Math.max(1, scale)
      for (const item of shown.values()) {
        const o = item.place.offset
        scratch.set(globeCentre.x + o.x * k, globeCentre.y + o.y * k, globeCentre.z + o.z * k)
        if (inside && inside.blend > 0) {
          around.set(
            inside.centre.x + inside.right.x * o.x + inside.toward.x * o.z,
            inside.centre.y + o.y,
            inside.centre.z + inside.right.z * o.x + inside.toward.z * o.z,
          )
          scratch.lerp(around, inside.blend)
        }
        item.mesh.position.copy(scratch)
        item.mesh.lookAt(viewer)
      }
    },
    pick(raycaster) {
      let best: { id: string; close: boolean; distance: number } | null = null
      for (const [id, item] of shown) {
        if (!item.hitTest) continue
        const hit = raycaster.intersectObject(item.mesh, false)[0]
        if (!hit?.uv || (best && hit.distance >= best.distance)) continue
        const what = item.hitTest({ x: hit.uv.x, y: hit.uv.y })
        if (what) best = { id, close: what === 'close', distance: hit.distance }
      }
      return best ? { id: best.id, close: best.close } : null
    },
    pressable(out) {
      for (const item of shown.values()) if (item.hitTest) out.push(item.mesh)
    },
    dispose() {
      for (const id of [...shown.keys()]) hide(id)
    },
  }
}
